import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { milestones } from '@/mocks/articles';

const ABOUT_FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9u2blt3pcjqs2fcskt0';

export default function About() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于诱人的飞行',
    description: '了解「诱人的飞行」的品牌故事与发展历程，获取联系我们信息。',
    url: 'https://airluxx.github.io/about',
    mainEntity: {
      '@type': 'Organization',
      name: '诱人的飞行',
      description: '专注于航空旅行体验的中文内容平台',
      foundingDate: '2018',
      url: 'https://airluxx.github.io/',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormState({ name: '', email: '', message: '' });
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch(ABOUT_FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormState({ name: '', email: '', message: '' });
      } else {
        const msg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (typeof parsed?.message === 'string' ? parsed.message : '') ||
          '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mt-6 md:mt-8">
              <h1 className="font-heading text-xl md:text-3xl font-semibold text-foreground-900">
                关于诱人的飞行
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                我们是一个专注于航空旅行体验的中文内容平台。从 2018 年创立至今，我们始终致力于发现和呈现全球最具吸引力的飞行方案——从顶级航空公司的奢华机舱，到跨越极圈的壮丽航线，从行李打包的实用技巧，到里程兑换的精算攻略。我们相信每一次起飞都应该成为旅程中值得期待的部分，而不仅仅是到达目的地的手段。
              </p>
            </section>

            <section id="milestones" className="mt-12 md:mt-14">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                品牌历程
              </h2>
              <div className="mt-6 relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-background-300/60 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, index) => (
                    <div key={index} className="flex items-start gap-4 md:gap-6">
                      <div className="hidden md:flex w-8 h-8 flex-shrink-0 rounded-full bg-accent-100 items-center justify-center relative z-10">
                        <span className="text-xs font-medium text-accent-700">{index + 1}</span>
                      </div>
                      <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-primary-600">{m.year}</span>
                          <h3 className="text-sm md:text-base font-medium text-foreground-900">{m.title}</h3>
                        </div>
                        <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="contact" className="mt-12 md:mt-14">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                如果你有任何航空旅行相关的问题、合作意向或反馈建议，欢迎随时与我们联系。
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm text-foreground-900">hello@yourendefeixing.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm text-foreground-900">诱人的飞行</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-red-packet-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">小红书</p>
                    <p className="text-sm text-foreground-900">@诱人的飞行</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="text-sm font-medium text-foreground-900 mb-4">发送消息</h3>

                {formStatus === 'success' ? (
                  <div className="px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                    <i className="ri-check-line mr-1"></i>
                    消息已发送！我们将在 1-2 个工作日内回复你。
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                        placeholder="你的姓名"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                        placeholder="你的邮箱"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        value={formState.message}
                        onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                        placeholder="你的留言（最多 500 字）"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      ></textarea>
                      <p className="mt-1 text-xs text-foreground-400">{formState.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    <div>
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </div>
                  </form>
                )}

                {formStatus === 'error' && (
                  <p className="mt-3 text-xs text-red-600">{formError}</p>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}